import { clampHeightCm } from "./components/app/heightUtils";
import type { Gender, Language, Preferences } from "./types";

const ADULT_MIN_AGE = 18;

type OnboardingPreferenceDefaults = Pick<
  Preferences,
  "ageYounger" | "ageOlder" | "minHeight" | "maxHeight" | "preferredLanguages"
>;

function uniqueLanguageNames(languages: readonly Language[]) {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const entry of languages) {
    const language = entry.language.normalize("NFKC").trim().replace(/\s+/g, " ");
    const key = language.toLocaleLowerCase("en-US");
    if (!language || seen.has(key)) continue;
    seen.add(key);
    result.push(language);
  }
  return result;
}

export function buildOnboardingPreferenceDefaults({
  gender,
  age,
  height,
  languages,
}: {
  gender: Gender;
  age: number;
  height: number;
  languages: readonly Language[];
}): OnboardingPreferenceDefaults {
  const desiredYearsYounger = gender === "female" ? 1 : 8;
  const desiredYearsOlder = gender === "female" ? 8 : 1;
  const centimetersBelowHeight = gender === "female" ? 5 : 20;
  const centimetersAboveHeight = gender === "female" ? 20 : 5;
  const availableAdultYears = Math.max(0, Math.floor(age) - ADULT_MIN_AGE);

  return {
    ageYounger: Math.min(desiredYearsYounger, availableAdultYears),
    ageOlder: desiredYearsOlder,
    minHeight: clampHeightCm(height - centimetersBelowHeight),
    maxHeight: clampHeightCm(height + centimetersAboveHeight),
    preferredLanguages: uniqueLanguageNames(languages),
  };
}
